import { Link } from "react-router-dom";
import MaskedReveal from "@/components/MaskedReveal";
import FadeUp from "@/components/FadeUp";
import { ArrowUpRight } from "lucide-react";
import { PORTRAITS, PORTRAIT_ALT } from "@/data/portraits";
import SEO from "@/components/SEO";
import JsonLd from "@/components/JsonLd";
import { physicianJsonLd, breadcrumbListJsonLd, medicalWebPageJsonLd } from "@/data/schema";

const TIMELINE = [
  ["Present","Principal Consultant — Interventional Radiology, Max Super Speciality Hospital, Mohali."],
  ["Fellowships","Advanced Vascular & Endovascular IR training across leading Indian institutions."],
  ["Memberships","ISVIR (Indian Society of Vascular & Interventional Radiology), CIRSE (Cardiovascular & Interventional Radiological Society of Europe), IRIA (Indian Radiological & Imaging Association)."],
  ["Registration","Punjab Medical Council (PMC), Medical Council of India (MCI)."],
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-28 md:pt-36 pb-28">
      <SEO
        title="About Dr Tegbir Singh Sidhu — Senior Interventional Radiologist, Mohali"
        description="Dr Tegbir Singh Sidhu is a senior Vascular & Endovascular Interventional Radiologist at Max Super Speciality Hospital, Mohali. Fellowships, memberships (ISVIR, CIRSE, IRIA), and the practice's commitment to one-doctor continuity."
        keywords={["Dr Tegbir Sidhu", "Interventional Radiologist Mohali", "Vascular Interventional Radiology Chandigarh", "senior IR India", "ISVIR CIRSE IRIA"]}
        url="/about"
        type="profile"
      />
      <JsonLd data={[
        physicianJsonLd(),
        medicalWebPageJsonLd({ name: "About Dr Tegbir Singh Sidhu", description: "Bio, credentials and memberships of Dr Tegbir Singh Sidhu, senior Interventional Radiologist at Max Super Speciality Hospital, Mohali.", url: "/about" }),
        breadcrumbListJsonLd([{ label: "Home", to: "/" }, { label: "About", to: "/about" }]),
      ]}/>
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6">
          <FadeUp className="col-span-12">
            <div className="kbd">— About</div>
          </FadeUp>

          <div className="col-span-12 md:col-span-8 mt-8">
            <h1 className="font-serif text-6xl md:text-8xl leading-[0.98] tracking-tightest">
              <MaskedReveal text={"A senior IR"} />
              <MaskedReveal text={"who returns"} delay={0.1} />
              <MaskedReveal text={"your calls."} delay={0.2} className="italic font-normal" />
            </h1>
          </div>

          <FadeUp className="col-span-12 md:col-span-4 self-end" delay={0.2}>
            <div className="aspect-[3/4] overflow-hidden hairline noise-overlay relative">
              <img src={PORTRAITS.ABOUT_HERO} alt={PORTRAIT_ALT} className="w-full h-full object-cover"/>
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none" />
            </div>
          </FadeUp>

          <FadeUp className="col-span-12 md:col-span-7 md:col-start-1 mt-16" delay={0.3}>
            <p className="text-lg md:text-xl leading-relaxed text-ink">
              Dr Tegbir Singh Sidhu is a senior interventional radiologist practising vascular and endovascular medicine at Max Super Speciality Hospital, Mohali. His practice is built on a single premise: the doctor who reviews your imaging is the doctor who treats you and who follows up. There are no handoffs, no substitutes, no call centres.
            </p>
            <p className="mt-6 text-lg md:text-xl leading-relaxed text-ink-muted">
              Trained in image-guided, minimally invasive techniques, Dr Sidhu treats over thirty vascular conditions — from aortic aneurysms and peripheral artery disease to dialysis access, fibroid embolisation, and interventional oncology. When a keyhole procedure is the right answer, it is used. When it isn't, he says so.
            </p>
          </FadeUp>

          <FadeUp className="col-span-12 md:col-span-5 md:col-start-8 mt-16" delay={0.4}>
            <div className="hairline-t pt-8 space-y-6">
              {TIMELINE.map(([k, v]) => (
                <div key={k}>
                  <div className="kbd">{k}</div>
                  <p className="mt-2 text-ink-muted leading-relaxed">{v}</p>
                </div>
              ))}
              <Link to="/contact" data-testid="about-cta" className="btn-pill-inv mt-4">Book a consultation <ArrowUpRight size={14}/></Link>
            </div>
          </FadeUp>
        </div>

        {/* In-practice editorial band — two portraits, asymmetric editorial layout */}
        <div data-testid="about-in-practice" className="mt-32 md:mt-40 grid grid-cols-12 gap-6">
          <FadeUp className="col-span-12 md:col-span-7">
            <div className="kbd">— In practice</div>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl leading-[1.05] tracking-tightest">
              Where the imaging is read,<br/>
              <span className="italic font-normal">the plan is written.</span>
            </h2>
            <p className="mt-8 text-lg text-ink-muted leading-relaxed max-w-xl">
              Same room. Same doctor. Same file. Every referral letter, every follow-up note, every phone call — written and returned by Dr Sidhu himself. The absence of a translation layer between imaging and treatment is what makes minimally invasive medicine work.
            </p>
          </FadeUp>

          <FadeUp className="col-span-12 md:col-span-6 md:mt-24" delay={0.15}>
            <div className="aspect-[4/5] overflow-hidden hairline noise-overlay relative">
              <img src={PORTRAITS.FULL_BODY_CORRIDOR} alt={PORTRAIT_ALT + " — Max Mohali corridor"} className="w-full h-full object-cover" loading="lazy"/>
              <div className="absolute bottom-4 left-4 right-4 text-bone">
                <div className="kbd text-white/80">Max Mohali · IR corridor</div>
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent pointer-events-none" />
            </div>
          </FadeUp>

          <FadeUp className="col-span-12 md:col-span-6 mt-8" delay={0.2}>
            <div className="aspect-[4/5] overflow-hidden hairline noise-overlay relative">
              <img src={PORTRAITS.COAT_EDITORIAL} alt={PORTRAIT_ALT + " — Interventional Radiology"} className="w-full h-full object-cover" loading="lazy"/>
              <div className="absolute bottom-4 left-4 right-4 text-bone">
                <div className="kbd text-white/80">Chambers · ISVIR · CIRSE · IRIA</div>
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent pointer-events-none" />
            </div>
          </FadeUp>

          <FadeUp className="col-span-12 mt-16" delay={0.3}>
            <div className="hairline-t pt-8">
              <div className="kbd">— The absence of handoffs</div>
              <p className="mt-4 text-ink-muted leading-relaxed">
                No coordinator screens your call. No junior reads the scan. No third party writes the report. This is not a marketing claim — it is the constraint the practice is designed around, and the reason NRI patients across six countries trust it for pre-arrival and post-return continuity.
              </p>
              <Link to="/why-choose" data-testid="about-why-choose-cta" className="btn-pill mt-8 inline-flex">Read the four chapters <ArrowUpRight size={14}/></Link>
            </div>
          </FadeUp>
        </div>
      </div>
    </div>
  );
}
